import type { Editor } from "@tiptap/core"
import { EditorState, Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"

import { TableHandlesPluginView } from "./tableHandlesPluginView"
import {
  createTableHandleElement,
  getCellsInColumn,
  getCellsInRow,
} from "./utils"

/** Plugin responsible for showing and hiding the row and column handles. */
export function tableHandlesPlugin(editor: Editor) {
  return new Plugin({
    key: new PluginKey("tableCellHandles"),
    view: (view) => new TableHandlesPluginView(view),
    state: {
      init: (_, state) => createDecorations({ editor, state }),
      apply: (tr, decorationSet, _, newState) => {
        if (!tr.docChanged) return decorationSet.map(tr.mapping, tr.doc)
        return createDecorations({ editor, state: newState })
      },
    },
    props: {
      decorations(state) {
        return this.getState(state)
      },
    },
  })
}

function createDecorations(args: { editor: Editor; state: EditorState }) {
  const { editor, state } = args
  const decorations: Decoration[] = []

  const firstColumnCells = getCellsInColumn(0)(state.selection)
  // Create a (row) handle button for each first cell of every row.
  if (firstColumnCells) {
    firstColumnCells.forEach(({ pos }, index) => {
      decorations.push(
        Decoration.widget(pos + 1, () => {
          return createTableHandleElement({ type: "row", editor, index })
        }),
      )
    })
  }

  const firstRowCells = getCellsInRow(0)(state.selection)
  // Create a (column) handle button for each cell of the first row.
  if (firstRowCells) {
    firstRowCells.forEach(({ pos }, index) => {
      decorations.push(
        Decoration.widget(pos + 1, () => {
          return createTableHandleElement({ type: "column", editor, index })
        }),
      )
    })
  }

  return DecorationSet.create(state.doc, decorations)
}
